import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Modal } from '../../../shared/Modal';
import { Accordion } from '../../../shared/Accordion';
import { useToast } from '../../../shared/Toast';
import { useAccount } from '../../../../hooks/useAccount';
import { useSchedulingOptions } from '../../../../hooks/useSchedulingOptions';
import { useDebounce } from '../../../../hooks/useDebounce';
import axiosInstance from '../../../../services/api';
import { sanitizeCaption, sanitizeHexColor, sanitizeInput } from '../../../../utils/sanitization';

// V2 Components (reused)
import { LivePreviewCanvas } from '../../LivePreviewCanvas';
import { CaptionEditor } from '../../CaptionEditor';
import { StylePanel } from '../../StylePanel';
import { AlignmentSelector } from '../../AlignmentSelector';
import { AIAssistant } from '../../AIAssistant';
import { SchedulingAccordion } from '../../SchedulingAccordion';
import GiphyPanel from '../../GiphyPanel';
import GIFControls from '../../GIFControls';
import '../../GiphyPanel.css';
import { CanvasColorEditor } from '../../CanvasColorEditor';
import { AspectRatioIndicator } from '../../AspectRatioIndicator';

// V3 Hooks and Sections
import { useImageApprovalState } from '../hooks/useImageApprovalState';
import { useChangeTracking } from '../hooks/useChangeTracking';
import { ModalActionFooter } from '../sections/ModalActionFooter';
import { DeleteAccordion } from '../sections/DeleteAccordion';
import { DEBOUNCE_CAPTION_PREVIEW_MS } from '../constants/defaults';

/**
 * ImageApprovalModal Component
 *
 * Modal for editing and approving Instagram Story images.
 * Uses the "Edit-First" UX paradigm with live preview.
 *
 * Features:
 * - Live preview with real-time updates
 * - Auto-saving caption editor
 * - Style customization (colors, fonts)
 * - Text position drag-and-drop
 * - GIF sticker support
 * - Canvas background for non-9:16 images
 * - AI-powered caption regeneration
 * - Scheduling options
 */
export function ImageApprovalModal({
  item,
  isOpen,
  onClose,
  onApprove,
  onUpdate,
  onRejectImage,
}) {
  const { t } = useTranslation('approval');
  const toast = useToast();
  const { selectedAccountId } = useAccount();

  // Scheduling options
  const scheduling = useSchedulingOptions(selectedAccountId, isOpen);

  // Consolidated image state from custom hook
  const imageState = useImageApprovalState(item, selectedAccountId, isOpen, toast, t);

  // Debounced caption for preview (prevents Chrome crash from rapid redraws)
  const debouncedCaptionForPreview = useDebounce(imageState.caption, DEBOUNCE_CAPTION_PREVIEW_MS);

  // Change tracking
  const changes = useChangeTracking({
    caption: imageState.caption,
    originalCaption: imageState.originalCaption,
    style: imageState.style,
    originalStyle: imageState.originalStyle,
    textPosition: imageState.textPosition,
    originalPosition: imageState.originalPosition,
    textAlignment: imageState.textAlignment,
    originalAlignment: imageState.originalAlignment,
    backgroundMode: imageState.backgroundMode,
    backgroundConfig: imageState.backgroundConfig,
    originalBackgroundConfig: imageState.originalBackgroundConfig,
    selectedFont: imageState.selectedFont,
    originalFont: imageState.originalFont,
    gifMetadata: imageState.gifMetadata,
    originalGifMetadata: imageState.originalGifMetadata,
  });

  // UI State
  const [isApproving, setIsApproving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Handle caption approval
   */
  const handleApprove = useCallback(async () => {
    setIsApproving(true);
    try {
      // Execute cascade reschedule if needed
      if (scheduling.cascadeInfo.needsCascade) {
        try {
          await scheduling.executeCascadeReschedule();
        } catch (cascadeError) {
          console.error('[ImageApprovalModal] Cascade reschedule failed:', cascadeError);
          toast.error(t('scheduling.cascadeError'));
          setIsApproving(false);
          return;
        }
      }

      const captionId = item.caption_id || item.current_caption_id;
      const sanitizedStyle = {
        ...imageState.style,
        textColor: sanitizeHexColor(imageState.style.textColor),
        backgroundColor: sanitizeHexColor(imageState.style.backgroundColor),
      };

      const scheduledTime = scheduling.isManualMode && scheduling.manualScheduledTime
        ? scheduling.manualScheduledTime.toISOString()
        : null;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'approve',
        instagram_account_id: selectedAccountId,
        caption: { text: sanitizeCaption(imageState.caption) },
        style: sanitizedStyle,
        ...(captionId && { previousCaptionId: captionId }),
        ...(scheduledTime && { scheduled_time: scheduledTime }),
      });

      toast.success(t('modal.toast.approved'));
      onApprove?.(item);
      onClose();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(t('modal.toast.approvalError', { error: error.message }));
    } finally {
      setIsApproving(false);
    }
  }, [item, imageState, scheduling, selectedAccountId, onApprove, onClose, toast, t]);

  /**
   * Handle AI regeneration
   */
  const handleRegenerate = useCallback(async ({ feedback, aiRequest }) => {
    setIsRegenerating(true);
    try {
      const requestText = typeof aiRequest === 'object' ? aiRequest.feedback : (aiRequest || feedback);
      const sanitizedRequest = sanitizeInput(requestText, 500);

      const response = await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'regenerate',
        instagram_account_id: selectedAccountId,
        aiRequest: sanitizedRequest,
      });

      if (response.data?.caption?.text) {
        imageState.setCaption(response.data.caption.text);
        toast.success(t('modal.toast.regenerated'));
      }

      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Regeneration error:', error);
      toast.error(t('modal.toast.regenerateError', { error: error.message }));
    } finally {
      setIsRegenerating(false);
    }
  }, [item, selectedAccountId, imageState, onUpdate, onClose, toast, t]);

  /**
   * Handle style regeneration (re-render with new colors/position)
   */
  const handleRegenerateWithStyle = useCallback(async () => {
    setIsRegenerating(true);
    try {
      const captionId = item.caption_id || item.current_caption_id;
      const sanitizedStyle = {
        ...imageState.style,
        textColor: sanitizeHexColor(imageState.style.textColor),
        backgroundColor: sanitizeHexColor(imageState.style.backgroundColor),
        textPosition: imageState.textPosition,
        textAlignment: imageState.textAlignment,
      };

      const payload = {
        action: 'regenerate',
        caption: { text: sanitizeCaption(imageState.caption) },
        style: sanitizedStyle,
        colorOnly: true,
        previousCaptionId: captionId,
        instagram_account_id: selectedAccountId,
        font_name: imageState.selectedFont,
      };

      // Add background config if changed
      if (changes.hasBackgroundChanges) {
        payload.backgroundUpdate = true;
        payload.backgroundConfig = {
          background_type: imageState.backgroundMode,
          ...(imageState.backgroundMode === 'solid'
            ? { background_color: imageState.backgroundConfig.backgroundColor }
            : {
                gradient_type: imageState.backgroundConfig.gradientType,
                gradient_colors: imageState.backgroundConfig.gradientColors,
                gradient_angle: imageState.backgroundConfig.gradientAngle,
              }
          ),
        };
      }

      await axiosInstance.post(`/captions/${item.image_id}/update`, payload);

      toast.success(t('modal.toast.rerendering'));
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Style regeneration error:', error);
      toast.error(t('modal.toast.regenerateError', { error: error.message }));
    } finally {
      setIsRegenerating(false);
    }
  }, [item, imageState, changes, selectedAccountId, onUpdate, onClose, toast, t]);

  /**
   * Handle image deletion
   */
  const handleDeleteImage = useCallback(async () => {
    const trimmedReason = deleteReason.trim();
    if (!trimmedReason) {
      toast.error(t('modal.delete.reasonRequired'));
      return;
    }

    setIsDeleting(true);
    try {
      await onRejectImage?.(item, trimmedReason);
      toast.success(t('modal.delete.success'));
      onClose();
    } catch (error) {
      console.error('Delete image error:', error);
      toast.error(t('modal.delete.failed', { error: error.message }));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteReason, item, onRejectImage, onClose, toast, t]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!item) return null;

  const isProcessing = isApproving || imageState.isSaving || isRegenerating;

  // Determine primary action based on changes
  const hasPendingChanges = changes.hasAnyChanges;
  const primaryAction = hasPendingChanges ? handleRegenerateWithStyle : handleApprove;
  const primaryLabel = hasPendingChanges
    ? t('modal.button.regenerate', { defaultValue: 'Re-render' })
    : t('modal.button.approve', { defaultValue: 'Approve' });
  const processingLabel = hasPendingChanges
    ? t('modal.button.regenerating', { defaultValue: 'Re-rendering...' })
    : t('modal.button.approving', { defaultValue: 'Approving...' });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      title={t('modal.title')}
      closeOnBackdrop={false}
      closeOnEscape={!isProcessing}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 -mx-6 -my-4 lg:h-[calc(100vh-160px)]">
        {/* Left Column - Preview */}
        <div className="lg:sticky lg:top-0 lg:self-start bg-gray-50 p-6 lg:h-[calc(100vh-160px)] flex items-center justify-center">
          <div className="w-full max-w-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">
              {t('modal.preview.title')}
            </h3>
            <div className="relative">
              <LivePreviewCanvas
                imageUrl={imageState.imageUrl}
                captionText={debouncedCaptionForPreview}
                style={imageState.style}
                textPosition={imageState.textPosition}
                originalPosition={imageState.originalPosition}
                onPositionChange={imageState.handlePositionChange}
                onResetPosition={imageState.handleResetPosition}
                textAreaWidth={imageState.textAreaWidth}
                textAlignment={imageState.textAlignment}
                fontSize={imageState.fontSize}
                isPositionOverridden={changes.hasPositionChanges}
                gifMetadata={imageState.gifMetadata}
                onGifChange={imageState.setGifMetadata}
                canvasBackgroundColor={imageState.canvasBackgroundColor}
                backgroundMode={imageState.backgroundMode}
                backgroundConfig={imageState.backgroundConfig}
                hasWrongAspectRatio={imageState.hasWrongAspectRatio}
                backgroundMetadata={item?.background_metadata}
                fontName={imageState.selectedFont}
              />
              <AspectRatioIndicator visible={imageState.hasWrongAspectRatio} />
            </div>
          </div>
        </div>

        {/* Right Column - Controls */}
        <div className="flex flex-col lg:h-[calc(100vh-160px)]">
          {/* Scrollable Content */}
          <div className="flex-1 p-6 space-y-6 overflow-y-auto overscroll-contain">
            {/* Text & Caption Accordion */}
            <Accordion
              title={t('modal.accordion.textCaptionTitle', { defaultValue: 'Text & Caption' })}
              icon="✏️"
              defaultExpanded={false}
              variant="neutral"
            >
              <div className="space-y-6 pt-4">
                {/* Caption Editor */}
                <CaptionEditor
                  initialCaption={imageState.caption}
                  imageId={item.image_id}
                  captionId={item.caption_id || item.current_caption_id}
                  instagramAccountId={selectedAccountId}
                  onChange={imageState.handleCaptionChange}
                  onSave={() => {}}
                />

                {/* Style Panel */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">
                    {t('modal.textStyling', { defaultValue: 'Text Styling' })}
                  </h4>
                  <StylePanel
                    style={imageState.style}
                    onChange={imageState.handleStyleChange}
                    imageUrl={imageState.imageUrl}
                    brandColors={imageState.brandColors}
                    selectedFont={imageState.selectedFont}
                    onFontChange={imageState.handleFontChange}
                    accountId={selectedAccountId}
                  />
                </div>

                {/* Alignment Selector */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">
                    {t('modal.textPosition', { defaultValue: 'Text Position' })}
                  </h4>
                  <AlignmentSelector
                    currentAlignment={imageState.textAlignment}
                    originalAlignment={imageState.originalAlignment}
                    onAlignmentChange={imageState.handleAlignmentChange}
                    disabled={isProcessing}
                  />
                </div>
              </div>
            </Accordion>

            {/* GIF Stickers Accordion */}
            <Accordion
              title={t('upload:gif.stickerLabel', { defaultValue: 'GIF Stickers' })}
              icon="✨"
              defaultExpanded={false}
              variant="neutral"
            >
              <div className="pt-4">
                <button
                  onClick={imageState.handleOpenGiphyPanel}
                  className="gif-sticker-btn w-full"
                  disabled={imageState.isSavingGif || imageState.isRemovingGif}
                >
                  <Sparkles size={16} />
                  {imageState.isSavingGif ? (
                    t('upload:gif.saving', { defaultValue: 'Saving...' })
                  ) : imageState.gifMetadata ? (
                    t('upload:gif.editButton', { count: 1, defaultValue: 'Edit GIF' })
                  ) : (
                    t('upload:gif.addButton', { defaultValue: 'Add GIF Sticker' })
                  )}
                </button>

                {/* GIF Controls Panel */}
                {imageState.gifMetadata && (
                  <div className="mt-4">
                    <GIFControls
                      gifMetadata={imageState.gifMetadata}
                      onChange={imageState.setGifMetadata}
                      onRemove={imageState.handleRemoveGif}
                      isRemoving={imageState.isRemovingGif}
                    />
                  </div>
                )}
              </div>
            </Accordion>

            {/* Canvas Background Accordion (only for non-9:16 images) */}
            {imageState.hasWrongAspectRatio && (
              <Accordion
                title={t('modal.accordion.canvasBackgroundTitle', { defaultValue: 'Canvas Background' })}
                icon="🎨"
                defaultExpanded={false}
                variant="info"
              >
                <div className="pt-4">
                  <CanvasColorEditor
                    backgroundMode={imageState.backgroundMode}
                    backgroundConfig={imageState.backgroundConfig}
                    originalBackgroundConfig={imageState.originalBackgroundConfig}
                    onModeChange={imageState.handleBackgroundModeChange}
                    onSolidColorChange={imageState.handleSolidColorChange}
                    onGradientChange={imageState.handleGradientChange}
                    imageUrl={imageState.imageUrl}
                    brandColors={imageState.brandColors}
                  />
                </div>
              </Accordion>
            )}

            {/* AI Assistant Section */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
              <AIAssistant
                currentCaption={imageState.caption}
                onRegenerate={handleRegenerate}
                isLoading={isRegenerating}
                imageId={item.image_id}
              />
            </div>

            {/* Scheduling Accordion */}
            <SchedulingAccordion
              autoScheduledTime={scheduling.autoScheduledTime}
              manualScheduledTime={scheduling.manualScheduledTime}
              scheduledTime={scheduling.scheduledTime}
              isManualMode={scheduling.isManualMode}
              cascadeInfo={scheduling.cascadeInfo}
              postIntervalHours={scheduling.postIntervalHours}
              isLoading={scheduling.isLoading}
              error={scheduling.error}
              onManualTimeChange={scheduling.setManualTime}
              onClearManualTime={scheduling.clearManualTime}
              disabled={isProcessing}
            />

            {/* Delete Image Accordion */}
            <DeleteAccordion
              variant="image"
              reason={deleteReason}
              onReasonChange={setDeleteReason}
              onDelete={handleDeleteImage}
              isDeleting={isDeleting}
              disabled={isProcessing}
            />
          </div>

          {/* Sticky Action Footer */}
          <ModalActionFooter
            hasChanges={hasPendingChanges}
            changeTypes={changes.changeTypes}
            isProcessing={isApproving || isRegenerating}
            disabled={imageState.isSaving || !imageState.caption.trim()}
            onCancel={handleClose}
            onPrimaryAction={primaryAction}
            primaryLabel={primaryLabel}
            processingLabel={processingLabel}
            showChangeLabels={hasPendingChanges}
          />
        </div>
      </div>

      {/* Giphy Panel Overlay */}
      {imageState.showGiphyPanel && (
        <GiphyPanel
          onSelectGif={imageState.handleSelectGif}
          onClose={() => imageState.setShowGiphyPanel(false)}
        />
      )}
    </Modal>
  );
}

ImageApprovalModal.propTypes = {
  item: PropTypes.shape({
    image_id: PropTypes.string.isRequired,
    caption_id: PropTypes.string,
    current_caption_id: PropTypes.string,
    caption_text: PropTypes.string,
    text_color_hex: PropTypes.string,
    text_background_color_hex: PropTypes.string,
    text_has_background: PropTypes.bool,
    text_position: PropTypes.object,
    text_alignment: PropTypes.string,
    font_name: PropTypes.string,
    background_metadata: PropTypes.object,
    GifStickerMetadata: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    sentiment: PropTypes.string,
    instagram_account_id: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func,
  onUpdate: PropTypes.func,
  onRejectImage: PropTypes.func,
};

export default ImageApprovalModal;
